import { AnvaPossiblePackage, AnvaQuote } from '@flows/src/types/anva';
import { create } from 'zustand';

export interface Package extends AnvaPossiblePackage {
  quotes: AnvaQuote[];
  due_date?: string;
  payment_interval?: string;
  total_premium_after_discount?: number;
  total_premium_before_discount?: number;
  discount_percentage?: number;
  discount?: number;
}

interface flowsProductBasketStore {
  packages: Package[];
  insurances: AnvaQuote[];

  // setters
  setPackages: (packages: Package[]) => void;
  setInsurances: (insurances: AnvaQuote[]) => void;

  // Add quotes to collections
  addToInsurances: (quote: AnvaQuote) => void;
  addToPackage: (packageCode: string, quote: AnvaQuote) => void;

  createPackage: (pack: AnvaPossiblePackage) => void;
  updatePackage: (packageCode: string, pack: Partial<Package>) => void;
  updateInsurance: (id: string, quote: Partial<AnvaQuote>) => void;

  // Remove quotes from collections
  removeFromInsurances: (quoteId: string) => void;
  removeFromPackage: (packageCode: string, quoteId: string) => void;
  removePackage: (packageCode: string) => void;

  // Move quotes between collections
  moveFromInsuranceToPackage: (packageCode: string, quoteId: string) => void;
  moveFromPackageToInsurance: (packageCode: string, quoteId: string) => void;
  moveToAnotherPackage: (
    fromPackageCode: string,
    toPackageCode: string,
    quoteId: string,
  ) => void;

  // Validation
  isBasketValidated: boolean;
  setIsBasketValidated: (isValidated: boolean) => void;

  // Reset store
  reset: () => void;
}

const useflowsProductBasketStore = create<flowsProductBasketStore>(
  (set, get) => ({
    packages: [],
    insurances: [],

    // Setters
    setPackages: (packages) => {
      set(() => ({
        packages,
        isBasketValidated: false,
      }));
    },
    setInsurances: (insurances) => {
      set(() => ({
        insurances,
        isBasketValidated: false,
      }));
    },

    // Add quotes to collections
    addToInsurances: (quote) =>
      set((state) => ({
        insurances: [...state.insurances, quote],
        isBasketValidated: false,
      })),

    addToPackage: (packageCode, quote) =>
      set((state) => {
        const packageIndex = state.packages.findIndex(
          (pkg) => pkg.code === packageCode,
        );
        if (packageIndex === -1) return state;

        const newPackages = [...state.packages];
        newPackages[packageIndex] = {
          ...newPackages[packageIndex],
          quotes: [...newPackages[packageIndex].quotes, quote],
        };

        return {
          packages: newPackages,
          isBasketValidated: false,
        };
      }),

    // Create a new package
    createPackage: (pack) =>
      set((state) => {
        // Check if package with this ID already exists
        if (state.packages.some((pkg) => pkg.code === pack.code)) return state;

        return {
          packages: [...state.packages, { ...pack, quotes: [] }],
          isBasketValidated: false,
        };
      }),
    updatePackage: (pkgCode, partialPkg) =>
      set((state) => {
        const packageIndex = state.packages.findIndex(
          (pkg) => pkg.code === pkgCode,
        );
        if (packageIndex === -1) return state;

        const newPackages = [...state.packages];
        newPackages[packageIndex] = {
          ...newPackages[packageIndex],
          ...partialPkg,
        };

        return {
          packages: newPackages,
          isBasketValidated: false,
        };
      }),
    updateInsurance: (id, quote) =>
      set((state) => {
        const insuranceIndex = state.insurances.findIndex(
          (ins) => ins.id === id,
        );
        if (insuranceIndex === -1) return state;
        const newInsurances = [...state.insurances];
        newInsurances[insuranceIndex] = {
          ...newInsurances[insuranceIndex],
          ...quote,
        };
        return {
          insurances: newInsurances,
          isBasketValidated: false,
        };
      }),
    // Remove quotes from collections
    removeFromInsurances: (quoteId) =>
      set((state) => ({
        insurances: state.insurances.filter((quote) => quote.id !== quoteId),
        isBasketValidated: false,
      })),

    removeFromPackage: (packageCode, quoteId) =>
      set((state) => {
        const packageIndex = state.packages.findIndex(
          (pkg) => pkg.code === packageCode,
        );
        if (packageIndex === -1) return state;

        const newPackages = [...state.packages];
        newPackages[packageIndex] = {
          ...newPackages[packageIndex],
          quotes: newPackages[packageIndex].quotes.filter(
            (quote) => quote.id !== quoteId,
          ),
        };
        // Remove package if it has no quotes left
        if (newPackages[packageIndex].quotes.length === 0) {
          newPackages.splice(packageIndex, 1);
        }
        return {
          packages: newPackages,
          isBasketValidated: false,
        };
      }),

    removePackage: (packageCode) =>
      set((state) => ({
        packages: state.packages.filter((pkg) => pkg.code !== packageCode),
        isBasketValidated: false,
      })),

    // Move quotes between collections
    moveFromInsuranceToPackage: (packageCode, quoteId) =>
      set((state) => {
        // Find the quote in insurances
        const quote = state.insurances.find((q) => q.id === quoteId);
        if (!quote) return state;

        // Find target package
        const packageIndex = state.packages.findIndex(
          (pkg) => pkg.code === packageCode,
        );
        if (packageIndex === -1) return state;

        // Remove from insurances
        const newInsurances = state.insurances.filter((q) => q.id !== quoteId);

        // Add to package
        const newPackages = [...state.packages];
        newPackages[packageIndex] = {
          ...newPackages[packageIndex],
          quotes: [...newPackages[packageIndex].quotes, quote],
        };

        return {
          insurances: newInsurances,
          packages: newPackages,
          isBasketValidated: false,
        };
      }),

    moveFromPackageToInsurance: (packageCode, quoteId) =>
      set((state) => {
        // Find the package
        const packageIndex = state.packages.findIndex(
          (pkg) => pkg.code === packageCode,
        );
        if (packageIndex === -1) return state;

        // Find the quote in the package
        const quote = state.packages[packageIndex].quotes.find(
          (q) => q.id === quoteId,
        );
        if (!quote) return state;

        // Remove from package
        const newPackages = [...state.packages];
        newPackages[packageIndex] = {
          ...newPackages[packageIndex],
          quotes: newPackages[packageIndex].quotes.filter(
            (q) => q.id !== quoteId,
          ),
        };

        // Add to insurances
        const newInsurances = [...state.insurances, quote];

        return {
          packages: newPackages,
          insurances: newInsurances,
          isBasketValidated: false,
        };
      }),

    moveToAnotherPackage: (fromPackageCode, toPackageCode, quoteId) =>
      set((state) => {
        const fromPackageIndex = state.packages.findIndex(
          (pkg) => pkg.code === fromPackageCode,
        );
        const toPackageIndex = state.packages.findIndex(
          (pkg) => pkg.code === toPackageCode,
        );

        if (fromPackageIndex === -1 || toPackageIndex === -1) return state;

        // Find the quote in source package
        const quote = state.packages[fromPackageIndex].quotes.find(
          (q) => q.id === quoteId,
        );
        if (!quote) return state;

        // Remove from source package
        const newPackages = [...state.packages];
        newPackages[fromPackageIndex] = {
          ...newPackages[fromPackageIndex],
          quotes: newPackages[fromPackageIndex].quotes.filter(
            (q) => q.id !== quoteId,
          ),
        };

        // Add to target package
        newPackages[toPackageIndex] = {
          ...newPackages[toPackageIndex],
          quotes: [...newPackages[toPackageIndex].quotes, quote],
        };

        // Remove package if it has no quotes left
        if (newPackages[fromPackageIndex].quotes.length === 0) {
          newPackages.splice(fromPackageIndex, 1);
        }

        return {
          packages: newPackages,
          isBasketValidated: false,
        };
      }),
    isBasketValidated: false,
    setIsBasketValidated: (isValidated) =>
      set(() => ({
        isBasketValidated: isValidated,
      })),

    // Reset store
    reset: () =>
      set(() => ({
        packages: [],
        insurances: [],
        isBasketValidated: false,
      })),
  }),
);

export default useflowsProductBasketStore;
